import { RolesEnums } from "~/utils/enums/roles.enums";

/**
 * How much each role can do — the scale of the backend's `getRolesPowers()` (orders
 * `shared-common/auth/guards/guards-utils.ts`), on which its 409 guard compares a user's highest role.
 * Keep the two in step when a role is added.
 *
 * SIMBEL_ADMIN has no rank here: a Log-As session appends it to the impersonated user's own roles
 * (auth store `fetchProfile`), and ranking it would make every change that user goes through look minor.
 * Actually gaining it is never minor, though — `isLowerRoleGain` rules that out on its own.
 */
const ROLE_RANKS: Record<Exclude<RolesEnums, RolesEnums.SIMBEL_ADMIN>, number> = {
  [RolesEnums.COMPANY_ADMIN]: 8,
  [RolesEnums.TRAINER_USER]: 7,
  [RolesEnums.GROUP_ADMIN]: 6,
  [RolesEnums.AUTHOR]: 5,
  [RolesEnums.TRAINING_ADMIN]: 5,
  [RolesEnums.TRAINING_PLAN_ADMIN]: 4,
  [RolesEnums.GROUP_VIEWER]: 3,
  [RolesEnums.MANAGER]: 2,
  [RolesEnums.EMPLOYEE]: 1,
  [RolesEnums.INTERNAL_TRAINER]: 1,
};

const rankOf = (role: RolesEnums): number => (role === RolesEnums.SIMBEL_ADMIN ? 0 : ROLE_RANKS[role]);

/**
 * Whether going from `before` to `after` only ADDS roles ranked below the highest one the user already
 * held — a group admin made referent of the session they just created, say. Rights below the ones the
 * user already has need no « Vos droits ont été modifiés » acknowledgement (Asana 1218772325394079).
 * A lost role always does: it can close the page the user is on.
 */
export function isLowerRoleGain(before: RolesEnums[], after: RolesEnums[]): boolean {
  if (before.some((role) => !after.includes(role))) return false;

  const gained = after.filter((role) => !before.includes(role));
  const highestBefore = Math.max(0, ...before.map(rankOf));

  return gained.length > 0 && gained.every((role) => role !== RolesEnums.SIMBEL_ADMIN && rankOf(role) < highestBefore);
}
